import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, UserRoundX, UserRoundCheck, Copy, ShieldCheck, GraduationCap, MailCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

function TeacherForm({ onSubmit, onCancel }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({ name, email, password: password || undefined });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 p-6 bg-white rounded-3xl card-tactile" data-testid="teacher-form">
      <h3 className="font-heading text-xl font-bold text-ink">Add Teacher</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-ink mb-1">Name</label>
          <input data-testid="teacher-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-bold text-ink mb-1">Email</label>
          <input data-testid="teacher-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-bold text-ink mb-1">Password (optional)</label>
          <input data-testid="teacher-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to email an invite link" className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          <p className="text-xs text-stone mt-1">If left blank, the teacher receives an email with a secure link to set their own password.</p>
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button type="button" data-testid="teacher-form-cancel" onClick={onCancel} className="h-12 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile">Cancel</button>
        <button data-testid="teacher-form-submit" type="submit" disabled={busy} className="h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
          {busy ? "Saving…" : "Add Teacher"}
        </button>
      </div>
    </form>
  );
}

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [inviteLink, setInviteLink] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      const { data } = await api.post("/admin/teachers", payload);
      if (data.invite_sent) {
        toast.success(`Invite emailed to ${data.email}`);
      } else if (data.set_password_link) {
        setInviteLink(data.set_password_link);
        toast.success("Teacher created — share the set-password link below");
      } else {
        toast.success("Teacher created");
      }
      setShowAdd(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/teachers/${t.id}`, { is_active: !t.is_active });
      toast.success(!t.is_active ? "Teacher activated" : "Teacher deactivated");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy it manually");
    }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Teachers</h1>
            <p className="text-stone mt-1">Teachers get full admin access to manage students, exercises and videos.</p>
          </div>
          <button
            data-testid="add-teacher-button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover"
          >
            <Plus className="w-5 h-5" /> Add Teacher
          </button>
        </div>

        {inviteLink && (
          <div className="mb-6 bg-lemon/25 border-2 border-lemon rounded-3xl p-5" data-testid="invite-link-banner">
            <div className="flex items-center gap-2 mb-2">
              <MailCheck className="w-5 h-5 text-ink" />
              <h2 className="font-heading text-lg font-bold text-ink">Set-password link (email delivery failed)</h2>
            </div>
            <p className="text-sm text-stone mb-3">Share this one-time link with the teacher. It expires in 30 minutes.</p>
            <div className="flex items-center gap-2">
              <code data-testid="invite-link-text" className="flex-1 truncate bg-white rounded-xl px-3 py-2 text-xs font-semibold text-ink">{inviteLink}</code>
              <button data-testid="copy-invite-link" onClick={copyLink} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white font-bold text-sm btn-tactile hover:brightness-110">
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
        )}

        {showAdd && (
          <div className="mb-6">
            <TeacherForm onSubmit={create} onCancel={() => setShowAdd(false)} />
          </div>
        )}

        <div className="bg-white rounded-3xl card-tactile overflow-hidden">
          {loading ? (
            <div className="p-8 text-stone">Loading…</div>
          ) : teachers.length === 0 ? (
            <div className="p-8 text-stone text-center">No teachers yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas">
                  <tr className="text-left text-xs uppercase tracking-wider text-stone">
                    <th className="p-4 font-bold">Name</th>
                    <th className="p-4 font-bold">Email</th>
                    <th className="p-4 font-bold">Role</th>
                    <th className="p-4 font-bold">Status</th>
                    <th className="p-4 font-bold">Added</th>
                    <th className="p-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="teachers-table" className="divide-y divide-cloud">
                  {teachers.map((t) => (
                    <tr key={t.id} data-testid={`teacher-row-${t.id}`} className="hover:bg-canvas/50">
                      <td className="p-4 font-semibold text-ink">{t.name}</td>
                      <td className="p-4 text-stone">{t.email}</td>
                      <td className="p-4">
                        {t.is_owner ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">
                            <ShieldCheck className="w-3.5 h-3.5" /> Owner
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-lemon/40 text-ink text-xs font-bold">
                            <GraduationCap className="w-3.5 h-3.5" /> Teacher
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${t.is_active ? "bg-leaf/15 text-leaf" : "bg-cloud text-stone"}`}>
                          {t.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="p-4 text-stone">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 justify-end">
                          {!t.is_owner && (
                            <button data-testid={`toggle-teacher-${t.id}`} onClick={() => toggleActive(t)} title={t.is_active ? "Deactivate" : "Activate"} className="p-2 rounded-full hover:bg-watermelon-light text-watermelon">
                              {t.is_active ? <UserRoundX className="w-4 h-4" /> : <UserRoundCheck className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
